import { entryArtistIds } from '@shared/lineupActs'
import type { Set as FestivalSet, Artist } from '../types'

/** A set or a lineup act — anything with performers and an optional billing. */
export interface LabelledAct {
  artistId?: string
  artistIds?: string[]
  title?: string
  collabArtistId?: string
}

export interface SetLabel {
  /** Who's playing ("A b2b B"), or the set's title when it has no artists or already names them. */
  name: string
  /** The set's title ("Space Jam Takeover") when it's shown alongside the artists rather than as the name. */
  title?: string
}

/**
 * How a set or lineup act is named on screen. The artists lead — people pick a
 * set by who's playing — and a showcase title rides along as a secondary line.
 * A title that already names an artist ("Excision (Detox Set)") stays the
 * name, since "Excision" over "Excision (Detox Set)" would just repeat itself.
 * A named collab ("Skull Machine") is the exception: it's billed as itself, so
 * its name leads and the members ride along ("BTSM x Kai Wachi").
 */
export function setLabel(set: LabelledAct, artists: Record<string, Artist>): SetLabel {
  const names = entryArtistIds(set).map(id => artists[id]?.name ?? id)
  const title = set.title?.trim()
  const collabName = set.collabArtistId ? (title || artists[set.collabArtistId]?.name) : undefined
  if (collabName) return names.length > 1 ? { name: collabName, title: names.join(' x ') } : { name: collabName }
  if (!title) return { name: names.join(' b2b ') }
  if (names.length === 0) return { name: title }
  const lower = title.toLowerCase()
  if (names.some(n => lower.includes(n.toLowerCase()))) return { name: title }
  return { name: names.join(' b2b '), title }
}

/** The set detail dialog's subtitle: "Space Jam Takeover · 19:00 – 21:30". */
export function setDetailSubtitle(
  set: FestivalSet,
  title: string | undefined,
  formatClockRange: (start: string, end: string) => string,
): string {
  const times = formatClockRange(set.startTime, set.endTime)
  return title ? `${title} · ${times}` : times
}
